import { AxiosResponse } from "axios";
import { IDeletionResult, addOrUpdateOrRemove } from "./services/storeHelper";
import { Action, Reducer } from "redux";
import { AppThunkAction, ApplicationState } from ".";
import { get, post } from "../fetch-interceptor";
import { catchApiError, defaultCatch, ErrorResponse, isCancel } from "./utils";
import { CostType, Dictionary, IExtensibleEntity, ITimeframe, IUserInfo, IWithAttributes } from "../entities/common";
import {
    KnownAction as NotificationsKnownAction,
    actionCreators as NotificationsActionCreators,
    NotificationType
} from "./NotificationsStore";
import { toDate, toDateTime, waitForFinalEvent } from "../components/utils/common";
import { nameof } from "./services/metadataService";
import { SourceType } from "./ExternalEpmConnectStore";
import { PPMFeatures, Subscription } from "./Tenant";
import { Richtext } from "../components/common/inputs/RichTextInput";
import { TimeReportingPeriod } from "./TimeReportingPeriodsStore";
import { ApprovalDecisionType } from "./ApprovalCenterStore";
import { IsTimesheetApprovalsEnabled, LoadTimeTrackingTaskDetails } from "../components/timeTracking/common";

export interface TimeTrackingLine extends IExtensibleEntity {
    TimeEntries: TimeTrackingEntry[];
    attributes: TimeTrackingLineAttr;
}

export interface TimeTrackingLineAttr extends Dictionary<any> {
    Project: TimeTrackingEntryProject;
    Task: { name: string; id: string; uniqueId?: string };

    TaskDetails?: TimeTrackingTaskDetails;
    ProjectDetails?: TimeTrackingProjectDetails;

    IsAdministrative?: boolean;
    IsCustomEvent?: boolean;
    SuggestionType?: TimeTrackingSuggestionType;
    HasSuggestedTasks?: boolean; //if has reported time but also exists in assignments or resource plan
    TotalReported: number;
    TimesheetLine?: TimesheetLine | null;
}

export enum TimeTrackingSuggestionType {
    None = 0,
    Assingments,
    ResourcePlan
}

export interface TimeTrackingEntry extends IExtensibleEntity {
    attributes: TimeTrackingEntryAttr;
    sourceInfos?: TimeTrackingEntrySourceInfo[] | null;
}

export interface TimeTrackingEntrySourceInfo {
    type: TimeTrackingEntrySourceType;
    sourceId: string;
    sourceType?: string;
}

export interface TimeTrackingEntryProject {
    name: string;
    id: string;
}

export interface TimeTrackingEntryAttr extends Dictionary<any> {
    Project?: TimeTrackingEntryProject;
    Task?: { name: string; id: string; uniqueId: string };
    CustomTimeEntryName?: string | null;
    Duration?: number;
    Description?: Richtext | null;
    Date: Date | string;
    IsBillable?: boolean;
    AdministrativeCategory?: string | null;
    CostType?: CostType;
}

export interface CreateTimeTrackingEntry {
    attributes: CreateTimeTrackingEntryAttr;
    sourceInfo?: TimeTrackingEntrySourceInfo;
}

export enum TimeTrackingEntrySourceType {
    O365,
    ExternalSuggestion,
    ResourcePlaSuggestion
}

export interface CreateTimeTrackingEntryAttr extends Dictionary<any> {
    Project?: { id: string };
    Task?: { id: string; uniqueId?: string };
    CustomTimeEntryName?: string | null;
    Duration?: number;
    Description?: Richtext | null;
    Date: Date | string;
    IsBillable?: boolean;
    AdministrativeCategory?: string | null;
    CostType?: CostType;
}

export interface ReportedForIntervalSummaryForInterval {
    project: number;
    administrative: number;
    start: Date;
    end: Date;
}

export enum TimesheetStatus {
    Draft = 0,
    InProgress = 1,
    PendingApproval = 2,
    Approved = 3,
    Rejected = 4
}

export enum TimesheetLineStatus {
    Draft = 0,
    InProgress = 1,
    PendingApproval = 2,
    Approved = 3,
    Rejected = 4
}

export function getTimesheetStatusLabel(status: TimesheetStatus): string {
    switch (status) {
        case TimesheetStatus.Draft:
            return "Draft";
        case TimesheetStatus.InProgress:
            return "In Progress";
        case TimesheetStatus.PendingApproval:
            return "Pending Approval";
        case TimesheetStatus.Approved:
            return "Approved";
        case TimesheetStatus.Rejected:
            return "Rejected";
        default:
            return "";
    }
}

export function getTimesheetLineStatusLabel(status: TimesheetLineStatus): string {
    switch (status) {
        case TimesheetLineStatus.Draft:
            return "Draft";
        case TimesheetLineStatus.InProgress:
            return "In Progress";
        case TimesheetLineStatus.PendingApproval:
            return "Pending Approval";
        case TimesheetLineStatus.Approved:
            return "Approved";
        case TimesheetLineStatus.Rejected:
            return "Rejected";
        default:
            return "";
    }
}

export interface ApprovalDecisionInfo {
    approver: IUserInfo;
    decision: ApprovalDecisionType;
    decisionDate: Date;
    comment: string | null;
}

export interface TimesheetLine {
    id: string;
    project: { id: string; name: string } | null;
    isAdministrative: boolean;
    status: TimesheetLineStatus;
    totalHours: number;
    approvers: IUserInfo[] | null;
    decision: ApprovalDecisionInfo | null;
}

export interface Timesheet {
    id: string;
    status: TimesheetStatus;
    lines: TimesheetLine[];
}

export interface TimeTrackingState {
    activeResourceId?: string;
    isLoading: boolean;
    isListLoading: boolean;
    isListUpdating: boolean;
    deletionResult?: IDeletionResult[];

    entries: TimeTrackingEntry[];

    tasks: TimeTrackingTaskDetails[];
    projects: TimeTrackingProjectDetails[];

    shownInterval?: ShownInterval;
    periodSummary?: ReportedForIntervalSummaryForInterval;
    monthSummary?: ReportedForIntervalSummaryForInterval;
    shownPeriod: TimeReportingPeriod | null;

    timesheet: Timesheet | null;
}

export interface TimeTrackingTaskDetails extends IExtensibleEntity {
    sourceType: SourceType | null;
    externalData: Dictionary<any>;
    uniqueId?: string;
    isDeleted?: boolean;
    isArchived?: boolean;
    lastModifiedDate: Date;
}

export interface TimeTrackingProjectDetails extends IExtensibleEntity {
    imageId?: string;
    isArchived?: boolean;
    isDeleted?: boolean;
    allowTimeTracking: boolean;
    name: string;
}

export interface ShownInterval {
    timeFrame: ITimeframe;
}

interface TimeTrackingEntriesUpdateResult extends GetTimeTrackingEntriesResult {
    removedIds: string[];
}

export interface GetTimeTrackingEntriesResult {
    entries: TimeTrackingEntry[];
    tasks: TimeTrackingTaskDetails[];
    projects: TimeTrackingProjectDetails[];
}

const unloadedState: TimeTrackingState = {
    isLoading: false,
    isListLoading: false,
    isListUpdating: false,
    entries: [],
    tasks: [],
    projects: [],
    shownPeriod: null,
    timesheet: null
};

enum ActionTypes {
    LOAD_TIME_TRACKING_ENTRIES = "LOAD_TIME_TRACKING_ENTRIES",
    TIME_TRACKING_ENTRIES_LOADED = "TIME_TRACKING_ENTRIES_LOADED",

    CREATE_TIME_TRACKING_ENTRY = "CREATE_TIME_TRACKING_ENTRY",

    UPDATE_TIME_TRACKING_ENTRIES = "UPDATE_TIME_TRACKING_ENTRIES",
    TIME_TRACKING_ENTRIES_UPDATED = "TIME_TRACKING_ENTRIES_UPDATED",
    TIME_TRACKING_TASKS_UPDATED = "TIME_TRACKING_TASKS_UPDATED",

    DELETE_TIME_TRACKING_ENTRY = "DELETE_TIME_TRACKING_ENTRY",
    TIME_TRACKING_ENTRY_DELETED = "TIME_TRACKING_ENTRY_DELETED",

    SET_TIME_TRACKING_INTERVAL = "SET_TIME_TRACKING_INTERVAL",

    LOAD_PERIOD_SUMMARY = "LOAD_PERIOD_SUMMARY",
    PERIOD_SUMMARY_LOADED = "PERIOD_SUMMARY_LOADED",
    LOAD_MONTH_SUMMARY = "LOAD_MONTH_SUMMARY",
    MONTH_SUMMARY_LOADED = "MONTH_SUMMARY_LOADED",

    SET_ACTIVE_RESOURCE = "SET_ACTIVE_RESOURCE",
    SET_SHOWN_PERIOD = "SET_SHOWN_PERIOD",

    SET_CURRENT_TIMESHEET = "SET_CURRENT_TIMESHEET"
}

interface LoadTimeTrackingEntries {
    type: ActionTypes.LOAD_TIME_TRACKING_ENTRIES;
}

interface TimeTrackingEntriesLoaded {
    type: ActionTypes.TIME_TRACKING_ENTRIES_LOADED;
    entries: TimeTrackingEntry[];
    tasks: TimeTrackingTaskDetails[];
    projects: TimeTrackingProjectDetails[];
}

interface CreateTimeTrackingEntries {
    type: ActionTypes.CREATE_TIME_TRACKING_ENTRY;
}

interface UpdateTimeTrackingEntries {
    type: ActionTypes.UPDATE_TIME_TRACKING_ENTRIES;
}

interface TimeTrackingEntriesUpdated {
    type: ActionTypes.TIME_TRACKING_ENTRIES_UPDATED;
    entries: TimeTrackingEntry[];
    removedIds: string[];
    tasks: TimeTrackingTaskDetails[];
    projects: TimeTrackingProjectDetails[];
}

interface TimeTrackingTasksUpdated {
    type: ActionTypes.TIME_TRACKING_TASKS_UPDATED;
    tasks: TimeTrackingTaskDetails[];
}

interface SetTimeTrackingInterval {
    type: ActionTypes.SET_TIME_TRACKING_INTERVAL;
    interval: ShownInterval;
}

interface LoadPeriodSummary {
    type: ActionTypes.LOAD_PERIOD_SUMMARY;
}

interface PeriodSummaryLoaded {
    type: ActionTypes.PERIOD_SUMMARY_LOADED;
    summary: ReportedForIntervalSummaryForInterval;
}

interface LoadMonthSummary {
    type: ActionTypes.LOAD_MONTH_SUMMARY;
}

interface MonthSummaryLoaded {
    type: ActionTypes.MONTH_SUMMARY_LOADED;
    summary: ReportedForIntervalSummaryForInterval;
}

interface SetActiveResource {
    type: ActionTypes.SET_ACTIVE_RESOURCE;
    resourceId: string | undefined;
}

interface SetShownPeriod {
    type: ActionTypes.SET_SHOWN_PERIOD;
    period: TimeReportingPeriod | null;
}

interface SetCurrentTimesheet {
    type: ActionTypes.SET_CURRENT_TIMESHEET;
    timesheet: Timesheet | null;
}

type KnownAction =
    | LoadTimeTrackingEntries
    | TimeTrackingEntriesLoaded
    | CreateTimeTrackingEntries
    | UpdateTimeTrackingEntries
    | TimeTrackingEntriesUpdated
    | TimeTrackingTasksUpdated
    | SetTimeTrackingInterval
    | LoadPeriodSummary
    | PeriodSummaryLoaded
    | LoadMonthSummary
    | MonthSummaryLoaded
    | SetActiveResource
    | SetShownPeriod
    | SetCurrentTimesheet;

const isDateInRange = (date: Date, start: Date, end: Date): boolean => {
    return date >= start && date <= end;
};

const reloadSummariesIfNeeded = (
    entries: TimeTrackingEntry[],
    dispatch: (action: KnownAction | AppThunkAction<KnownAction>) => void,
    getState: () => ApplicationState
): void => {
    const { periodSummary, monthSummary } = getState().timeTracking;
    const entryDates = entries.map(_ => toDate(_.attributes.Date)!);

    if (periodSummary) {
        const needsReload = entryDates.some(_ => isDateInRange(_, periodSummary.start, periodSummary.end));
        if (needsReload) {
            actionCreators.loadPeriodSummary(periodSummary.start, periodSummary.end)(dispatch, getState);
        }
    }

    if (monthSummary) {
        const needsReload = entryDates.some(_ => isDateInRange(_, monthSummary.start, monthSummary.end));
        if (needsReload) {
            actionCreators.loadMonthSummary(monthSummary.start, monthSummary.end)(dispatch, getState);
        }
    }
};

export const actionCreators = {
    loadTimeEntries:
        (start: Date, end: Date): AppThunkAction<KnownAction> =>
        (dispatch, getState) => {
            dispatch({ type: ActionTypes.LOAD_TIME_TRACKING_ENTRIES });

            get<GetTimeTrackingEntriesResult>(`/api/timetracking/resource/${_getActiveResourceId(getState)}/entries`, {
                start: start.toDateOnlyString(),
                end: end.toDateOnlyString()
            })
                .then(data => dispatch({ type: ActionTypes.TIME_TRACKING_ENTRIES_LOADED, ...data }))
                .catch(defaultCatch(dispatch));
        },

    addTimeEntries:
        (
            toCreate: CreateTimeTrackingEntry[],
            reloadSummaries: boolean = true,
            callback?: (entries: TimeTrackingEntry[]) => void,
            onErrorCallback?: () => void
        ): AppThunkAction<KnownAction | NotificationsKnownAction> =>
        (dispatch, getState) => {
            dispatch({ type: ActionTypes.CREATE_TIME_TRACKING_ENTRY });

            toCreate.forEach(_ => _fixEntryDate(_.attributes));

            post<TimeTrackingEntriesUpdateResult>(
                `/api/timetracking/resource/${_getActiveResourceId(getState)}/entry/bulk`,
                toCreate
            )
                .then(data => {
                    dispatch({ type: ActionTypes.TIME_TRACKING_ENTRIES_UPDATED, ...data });

                    if (reloadSummaries) {
                        reloadSummariesIfNeeded(data.entries, dispatch, getState);
                    }

                    callback?.(data.entries);
                })
                .catch(
                    catchApiError(_ => {
                        dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }));
                        dispatch(emptyUpdateResult);
                        onErrorCallback?.();
                    })
                );
        },

    updateTimeEntries:
        (
            updates: { id: string; attributes: Partial<TimeTrackingEntryAttr> }[],
            callback?: (entries: TimeTrackingEntry[]) => void
        ): AppThunkAction<KnownAction | NotificationsKnownAction> =>
        (dispatch, getState) => {
            dispatch({ type: ActionTypes.UPDATE_TIME_TRACKING_ENTRIES });

            post<TimeTrackingEntriesUpdateResult>(`/api/timetracking/entry/bulk/update`, updates)
                .then(data => {
                    dispatch({ type: ActionTypes.TIME_TRACKING_ENTRIES_UPDATED, ...data });

                    reloadSummariesIfNeeded(data.entries, dispatch, getState);

                    callback?.(data.entries);
                })
                .catch(
                    catchApiError(_ => {
                        dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }));
                        dispatch(emptyUpdateResult);
                    })
                );
        },

    deleteTimeEntries:
        (ids: string[], callback?: () => void): AppThunkAction<KnownAction | NotificationsKnownAction> =>
        (dispatch, getState) => {
            const stateBefore = getState().timeTracking;
            const deletedEntries = stateBefore.entries.filter(_ => ids.includes(_.id));

            post<TimeTrackingEntriesUpdateResult>(`/api/timetracking/entry/bulk/delete`, { ids })
                .then(data => {
                    dispatch({ type: ActionTypes.TIME_TRACKING_ENTRIES_UPDATED, ...data });

                    reloadSummariesIfNeeded(deletedEntries, dispatch, getState);

                    callback?.();
                })
                .catch(
                    catchApiError(_ => {
                        dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }));
                        dispatch(emptyUpdateResult);
                    })
                );
        },

    handleTaskRecalculationFinished:
        (taskIds: string[]): AppThunkAction<KnownAction> =>
        (dispatch, getState) => {
            taskIds.forEach(id => _pendingRecalculationTaskIds.add(id));
            _flushTaskRecalculation(dispatch, getState);
        },

    loadPeriodSummary:
        (start: Date, end: Date): AppThunkAction<KnownAction> =>
        (dispatch, getState) => {
            dispatch({ type: ActionTypes.LOAD_PERIOD_SUMMARY });

            get<ReportedForIntervalSummaryForInterval>(
                `/api/timetracking/resource/${_getActiveResourceId(getState)}/summary`,
                {
                    start: start.toDateOnlyString(),
                    end: end.toDateOnlyString()
                },
                { requestKey: "loadPeriodSummary" }
            )
                .then(apiResponse => {
                    dispatch({
                        type: ActionTypes.PERIOD_SUMMARY_LOADED,
                        summary: {
                            ...apiResponse,
                            start: start,
                            end: end
                        }
                    });
                })
                .catch(defaultCatch(dispatch));
        },

    loadMonthSummary:
        (start: Date, end: Date): AppThunkAction<KnownAction> =>
        (dispatch, getState) => {
            dispatch({ type: ActionTypes.LOAD_MONTH_SUMMARY });

            get<ReportedForIntervalSummaryForInterval>(
                `/api/timetracking/resource/${_getActiveResourceId(getState)}/summary`,
                {
                    start: start.toDateOnlyString(),
                    end: end.toDateOnlyString()
                },
                { requestKey: "loadMonthSummary" }
            )
                .then(apiResponse => {
                    dispatch({
                        type: ActionTypes.MONTH_SUMMARY_LOADED,
                        summary: {
                            ...apiResponse,
                            start: start,
                            end: end
                        }
                    });
                })
                .catch(defaultCatch(dispatch));
        },

    setShowInterval:
        (interval: ShownInterval): AppThunkAction<KnownAction> =>
        dispatch => {
            dispatch({ type: ActionTypes.SET_TIME_TRACKING_INTERVAL, interval });
        },
    setActiveResource:
        (resourceId: string | undefined): AppThunkAction<KnownAction> =>
        dispatch => {
            dispatch({ type: ActionTypes.SET_ACTIVE_RESOURCE, resourceId });
        },
    setShownPeriod:
        (period: TimeReportingPeriod | null): AppThunkAction<KnownAction> =>
        dispatch => {
            dispatch({ type: ActionTypes.SET_SHOWN_PERIOD, period });
        },

    setCurrentTimesheet:
        (timesheet: Timesheet | null): AppThunkAction<KnownAction> =>
        dispatch => {
            dispatch({ type: ActionTypes.SET_CURRENT_TIMESHEET, timesheet });
        },

    loadTimesheet:
        (resourceId: string, periodId: string): AppThunkAction<KnownAction> =>
        dispatch => {
            get<Timesheet>(`/api/timesheet/resource/${resourceId}/period/${periodId}`, undefined, {
                requestKey: "loadTimesheet"
            })
                .then(data => dispatch({ type: ActionTypes.SET_CURRENT_TIMESHEET, timesheet: data }))
                .catch(defaultCatch(dispatch));
        },

    selectPeriod:
        (period: TimeReportingPeriod): AppThunkAction<KnownAction> =>
        (dispatch, getState) => {
            const state = getState();
            const activeResourceId = _getActiveResourceId(getState);

            dispatch({ type: ActionTypes.SET_SHOWN_PERIOD, period });
            dispatch({
                type: ActionTypes.SET_TIME_TRACKING_INTERVAL,
                interval: {
                    timeFrame: {
                        start: period.attributes.StartDate,
                        end: period.attributes.EndDate
                    }
                }
            });
            dispatch({ type: ActionTypes.SET_CURRENT_TIMESHEET, timesheet: null });

            actionCreators.loadPeriodSummary(period.attributes.StartDate, period.attributes.EndDate)(dispatch, getState);

            if (IsTimesheetApprovalsEnabled(state.tenant) && period.attributes.ApprovalRequired) {
                actionCreators.loadTimesheet(activeResourceId, period.id)(dispatch, getState);
            }
        },

    submitTimesheet:
        (
            periodId: string,
            comment: string,
            onValidationErrors?: (messages: string[]) => void
        ): AppThunkAction<KnownAction | NotificationsKnownAction> =>
        (dispatch, getState) => {
            const resourceId = _getActiveResourceId(getState);
            post<Timesheet>(`/api/timesheet/resource/${resourceId}/period/${periodId}/submit`, { lineIds: [], comment })
                .then(data => {
                    dispatch({ type: ActionTypes.SET_CURRENT_TIMESHEET, timesheet: data });
                    const interval = getState().timeTracking.shownInterval;
                    if (interval) {
                        actionCreators.loadTimeEntries(interval.timeFrame.start, interval.timeFrame.end)(dispatch, getState);
                    }
                })
                .catch(error => _handleTimesheetError(error, dispatch, getState, resourceId, periodId, onValidationErrors));
        },

    recallTimesheet:
        (
            periodId: string,
            lineIds: string[],
            onValidationErrors?: (messages: string[]) => void,
            callback?: () => void
        ): AppThunkAction<KnownAction | NotificationsKnownAction> =>
        (dispatch, getState) => {
            const resourceId = _getActiveResourceId(getState);
            post<Timesheet>(`/api/timesheet/resource/${resourceId}/period/${periodId}/recall`, { lineIds })
                .then(data => {
                    dispatch({ type: ActionTypes.SET_CURRENT_TIMESHEET, timesheet: data });
                    callback?.();
                })
                .catch(error => {
                    _handleTimesheetError(error, dispatch, getState, resourceId, periodId, onValidationErrors);
                    callback?.();
                });
        }
};

const emptyUpdateResult: TimeTrackingEntriesUpdated = {
    type: ActionTypes.TIME_TRACKING_ENTRIES_UPDATED,
    entries: [],
    removedIds: [],
    tasks: [],
    projects: []
};

const _fixEntryDate = (attributes: CreateTimeTrackingEntryAttr) => {
    if (attributes.Date instanceof Date) {
        attributes.Date = attributes.Date.toDateOnlyString();
    }
};

const _handleTimesheetError = (
    error: unknown,
    dispatch: (
        action: KnownAction | NotificationsKnownAction | AppThunkAction<KnownAction | NotificationsKnownAction>
    ) => void,
    getState: () => ApplicationState,
    resourceId: string,
    periodId: string,
    onValidationErrors?: (messages: string[]) => void
): void => {
    if (isCancel(error)) {
        return;
    }

    const response = (error as { response?: AxiosResponse<ErrorResponse> }).response;
    if (typeof response?.data === "object" && response.data?.errors?.length) {
        onValidationErrors?.(response.data.errors.map(e => e.message));
    } else {
        catchApiError(_ => {
            dispatch(NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error }));
        })(error);
    }
    actionCreators.loadTimesheet(resourceId, periodId)(dispatch, getState);
};

const _getActiveResourceId = (getState: () => ApplicationState): string => {
    const resourceId = getState().timeTracking.activeResourceId;

    if (resourceId) {
        return resourceId;
    }

    throw new Error("Active resource is not set");
};

const _mergeTasksByLastModified = (
    existing: TimeTrackingTaskDetails[],
    incoming: TimeTrackingTaskDetails[]
): TimeTrackingTaskDetails[] => {
    if (!incoming.length) {
        return existing;
    }

    const byId = new Map(existing.map(_ => [_.id, _] as [string, TimeTrackingTaskDetails]));
    incoming.forEach(task => {
        const current = byId.get(task.id);
        if (!current || task.lastModifiedDate >= current.lastModifiedDate) {
            byId.set(task.id, task);
        }
    });
    return Array.from(byId.values());
};

const _pendingRecalculationTaskIds = new Set<string>();

const _flushTaskRecalculation = waitForFinalEvent(
    (dispatch: (action: any) => void, getState: () => ApplicationState) => {
        const ids = Array.from(_pendingRecalculationTaskIds);
        _pendingRecalculationTaskIds.clear();

        const loadedTaskIds = new Set(getState().timeTracking.tasks.map(_ => _.id));
        const toRefetch = ids.filter(_ => loadedTaskIds.has(_));

        if (toRefetch.length === 0) {
            return;
        }

        LoadTimeTrackingTaskDetails(toRefetch).then(tasks =>
            dispatch({ type: ActionTypes.TIME_TRACKING_TASKS_UPDATED, tasks })
        );
    },
    500,
    "timeTracking_taskRecalculationFinished"
);

const defaultReducer: Reducer<TimeTrackingState> = (state: TimeTrackingState, incomingAction: Action) => {
    const action = incomingAction as KnownAction;

    switch (action.type) {
        case ActionTypes.LOAD_TIME_TRACKING_ENTRIES: {
            return {
                ...state,
                isLoading: true,
                isListLoading: true
            };
        }
        case ActionTypes.TIME_TRACKING_ENTRIES_LOADED:
            action.entries.forEach(_ => _checkEntryDates(_));
            action.tasks.forEach(_checkTaskDates);

            return {
                ...state,
                isLoading: false,
                isListLoading: false,
                entries: action.entries,
                tasks: action.tasks,
                projects: action.projects
            };

        case ActionTypes.TIME_TRACKING_ENTRIES_UPDATED:
            action.entries.forEach(_ => _checkEntryDates(_));
            action.tasks.forEach(_checkTaskDates);

            return {
                ...state,
                entries: addOrUpdateOrRemove(state.entries, action.entries, action.removedIds),
                tasks: _mergeTasksByLastModified(state.tasks, action.tasks),
                projects: addOrUpdateOrRemove(state.projects, action.projects, []),
                isLoading: false,
                isListLoading: false
            };

        case ActionTypes.TIME_TRACKING_TASKS_UPDATED:
            action.tasks.forEach(_checkTaskDates);

            return {
                ...state,
                tasks: _mergeTasksByLastModified(state.tasks, action.tasks)
            };

        case ActionTypes.SET_TIME_TRACKING_INTERVAL:
            return {
                ...state,
                shownInterval: action.interval
            };

        case ActionTypes.PERIOD_SUMMARY_LOADED:
            return {
                ...state,
                periodSummary: action.summary
            };

        case ActionTypes.MONTH_SUMMARY_LOADED:
            return {
                ...state,
                monthSummary: action.summary
            };

        case ActionTypes.SET_ACTIVE_RESOURCE: {
            if (state.activeResourceId === action.resourceId) {
                return state;
            }

            return {
                ...unloadedState,
                activeResourceId: action.resourceId
            };
        }

        case ActionTypes.SET_SHOWN_PERIOD:
            return {
                ...state,
                shownPeriod: action.period
            };

        case ActionTypes.SET_CURRENT_TIMESHEET:
            return {
                ...state,
                timesheet: action.timesheet
            };

        default:
            return state;
    }
};

const _checkEntryDates = (entry: TimeTrackingEntry) => {
    entry.attributes.Date = toDate(entry.attributes.Date)!;
};

const _checkTaskDates = (task: TimeTrackingTaskDetails) => {
    task.lastModifiedDate = toDateTime(task.lastModifiedDate)!;
};

export const reducer: Reducer<TimeTrackingState> = (state: TimeTrackingState = unloadedState, incomingAction: Action) => {
    return defaultReducer(state, incomingAction);
};

export const DefaultCostType = CostType.Opex;

export const GetCostType = (taskOrProject: IWithAttributes | undefined): CostType => {
    return taskOrProject?.attributes[nameof<TimeTrackingEntryAttr>("CostType")] ?? DefaultCostType;
};

const GetIsBillable = (taskOrProject: IWithAttributes | undefined): boolean => {
    return !!taskOrProject?.attributes[nameof<TimeTrackingEntryAttr>("IsBillable")];
};

export const CopyBillableAndCostType = (
    entryAttributes: { IsBillable?: boolean; CostType?: CostType },
    taskOrProject: IWithAttributes | undefined,
    isEnterpriseTimeTracker: boolean
): void => {
    entryAttributes.IsBillable = GetIsBillable(taskOrProject);

    if (isEnterpriseTimeTracker) {
        entryAttributes.CostType = GetCostType(taskOrProject);
    }
};

export const TimelineToCreateTimeEntry = (
    timeTrackingLine: TimeTrackingLine,
    date: Date | string,
    duration: number
): CreateTimeTrackingEntryAttr => {
    return {
        Date: date,
        Duration: duration,

        Project: timeTrackingLine.attributes.IsAdministrative ? undefined : timeTrackingLine.attributes.Project,
        Task:
            timeTrackingLine.attributes.IsCustomEvent || timeTrackingLine.attributes.IsAdministrative ?
                undefined
            :   timeTrackingLine.attributes.Task,
        CustomTimeEntryName: timeTrackingLine.attributes.IsCustomEvent ? timeTrackingLine.attributes.Task.name : undefined,
        AdministrativeCategory:
            timeTrackingLine.attributes.IsAdministrative ? timeTrackingLine.attributes.Task.id : undefined
    };
};

export const IsEnterpriseTimeTracker = (subscription: Subscription) => {
    return Subscription.contains(subscription, PPMFeatures.TimeTrackingEnterprise);
};
